import {Component, OnInit, Inject, DestroyRef, inject} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {LocationStatusService} from '@emm/core/services/location-status.service';
import {TagService} from '@emm/core/services/tag.service';
import {BehaviorSubject} from "rxjs";

@Component({
    selector: 'emm-add-lock-item-dialog',
    styleUrls: ['./add-lock-item-dialog.component.scss'],
    templateUrl: './add-lock-item-dialog.component.html',
    standalone: false
})
export class AddLockItemDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    form: FormGroup;
    isReadOnly$ = this.authFacade.isReadOnly$;
    name: string;
    showStatusMessage$ = new BehaviorSubject<boolean>(false);
    showSubCategory$ = new BehaviorSubject<boolean>(false);

    categories = [
        'Master Menu Selection',
        'Optional Menu Selection',
        'Equipment Failure',
        'Ingredient Shortage',
    ];

    subCategories = [
        'MMS Restrictions',
        'MMS Options',
    ];

    subTypes = [
        { value: 'REMOVAL', label: 'Item Removal' },
        { value: 'MESSAGING', label: 'Status Messaging' },
        { value: 'SELECTED', label: 'Item Selection' },
    ];

    constructor(
        public authFacade: AuthFacadeService,
        public dialogRef: MatDialogRef<AddLockItemDialogComponent>,
        public locationStatusService: LocationStatusService,
        public tagService: TagService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.buildForm();
    }

    ngOnInit(): void {
        this.dialogRef.updatePosition({ top: '100px' });

        this.form.get('subType')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.updateStatusMessageValidation(value);
                this.showStatusMessage$.next(value === 'MESSAGING');
            });

        this.form.get('category')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.updateSubCategoryValidation(value);
                this.showSubCategory$.next(value.includes('Master Menu'));
            });

        const initialTypeValue = this.form.get('subType')?.value;
        this.showStatusMessage$.next(initialTypeValue === 'MESSAGING');
        const initialCategoryValue = this.form.get('formField')?.value;
        this.showSubCategory$.next(initialCategoryValue?.includes('Master Menu'));
    }

    buildForm(): void {
        this.form = new FormGroup({
            name: new FormControl(''),
            tag: new FormControl(''),
            subType: new FormControl(''),
            category: new FormControl(''),
            subCategory: new FormControl(''),
            statusMessage: new FormControl('')
        });
    }

    private updateStatusMessageValidation(triggerValue: string): void {
        const statusMessage = this.form.get('statusMessage');

        if (triggerValue === 'MESSAGING') {
            statusMessage?.setValidators([Validators.required]);
        } else {
            statusMessage?.clearValidators();
            statusMessage?.reset();
        }

        statusMessage?.updateValueAndValidity();
    }

    private updateSubCategoryValidation(triggerValue: string): void {
        const subCategory = this.form.get('subCategory');

        if (triggerValue.includes('Master Menu')) {
            subCategory?.setValidators([Validators.required]);
        } else {
            subCategory?.clearValidators();
            subCategory?.reset();
        }

        subCategory?.updateValueAndValidity();
    }

    closeAfterSubmit(): void {
        this.dialogRef.close('submit');
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    findTagExists(): void {
        if (this.form.controls.tag.valid) {
            this.tagService.getLocationStatusTags(this.form.get('tag').value)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(res => {
                if (res) {
                    this.form.controls.tag.setErrors({ tagExists: true });
                }
            });
        }
    }

    selectSubType(subType): void {
        if (subType !== 'MESSAGING') {
            this.form.controls.statusMessage.setValue(null);
        }
    }

    OnSubmit(): void {
        if (this.form.valid) {
            const locationStatus = {
                name: this.form.controls.name.value,
                tag: this.form.controls.tag.value,
                type: 'EMERGENCY',
                subType: this.form.controls.subType.value,
                statusMessage: this.form.controls.statusMessage.value,
                category: this.form.controls.category.value,
                subCategory: this.form.controls.subCategory.value,
            };
            this.locationStatusService.createLocationStatus(locationStatus)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(() => {
                this.closeAfterSubmit();
            });
        }
    }
}
